import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import SEO from '../components/SEO';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // In a real application, this would send data to backend
    setSubmitted(true);
    setTimeout(() => {
      setSubmitted(false);
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    }, 3000);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Contact Us | Interskate Self Storage - Lewisville, TX"
        description="Contact Interskate Self Storage in Lewisville, TX. Call (469) 251-6255, email us, or visit us at 1303 S Stemmons Fwy. Office hours Mon-Fri 9am-5pm, Sat 9am-1pm."
        canonical="/contact"
      />
      {/* Header */}
      <section className="bg-green-600 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4" data-testid="contact-title">
            Contact Us
          </h1>
          <p className="text-xl text-green-100">
            We're here to help! Reach out with any questions about our storage solutions.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-lg shadow-lg p-8" data-testid="contact-form">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Send Us a Message</h2>

                {submitted && (
                  <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-md" data-testid="success-message">
                    <p className="text-green-800">Thank you! Your message has been sent. We'll get back to you soon.</p>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                        Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                        data-testid="name-input"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                        data-testid="email-input"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                        Phone
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                        data-testid="phone-input"
                      />
                    </div>
                    <div>
                      <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                        Subject *
                      </label>
                      <input
                        type="text"
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                        data-testid="subject-input"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows="6"
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                      data-testid="message-input"
                    ></textarea>
                  </div>

                  <button
                    type="submit"
                    className="w-full md:w-auto inline-flex items-center justify-center px-8 py-3 text-base font-medium text-white bg-green-600 rounded-md hover:bg-green-700 transition-colors shadow-md"
                    data-testid="submit-button"
                  >
                    <Send className="mr-2 h-5 w-5" />
                    Send Message
                  </button>
                </form>
              </div>

            </div>

            {/* Contact Info */}
            <div className="space-y-6">
              <div className="bg-white rounded-lg shadow-lg p-6" data-testid="contact-info">
                <h2 className="text-xl font-bold text-gray-900 mb-6">Contact Information</h2>

                <div className="space-y-6">
                  <div className="flex items-start">
                    <MapPin className="h-6 w-6 text-green-600 mr-3 mt-1 flex-shrink-0" />
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 mb-1">Location</h3>
                      <p className="text-gray-700">
                        1303 S Stemmons Fwy<br />
                        Lewisville, TX 75067
                      </p>
                      <a href="#map" className="text-green-600 hover:underline mt-1 block text-sm">
                        View on Map
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <Phone className="h-6 w-6 text-green-600 mr-3 mt-1 flex-shrink-0" />
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 mb-1">Phone</h3>
                      <p className="text-gray-700">(469) 251-6255</p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <Mail className="h-6 w-6 text-green-600 mr-3 mt-1 flex-shrink-0" />
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 mb-1">Email</h3>
                      <p className="text-gray-700">interskate.selfstorage@gmail.com</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Map Section */}
              <div id="map" className="bg-white rounded-lg shadow-lg overflow-hidden mt-6">
                <iframe
                  src="https://maps.google.com/maps?q=1303%20S%20Stemmons%20Fwy%2C%20Lewisville%2C%20TX%2075067&t=&z=15&ie=UTF8&iwloc=&output=embed"
                  width="100%"
                  height="500"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Interskate Self Storage Location"
                  className="w-full h-[500px]"
                ></iframe>
              </div>

              <div className="bg-green-50 rounded-lg p-6 border border-green-100">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Office Hours</h3>
                <div className="text-gray-700 space-y-1">
                  <p>Mon-Fri: 9:00 AM - 5:00 PM</p>
                  <p>Sat: 9:00 AM - 1:00 PM</p>
                  <p>Sun: Closed</p>
                  <br />
                  <p className="mt-1"><strong>Gate Access:</strong></p>
                  <p>6:00 AM - 9:00 PM Daily</p>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Quick Actions</h3>
                <div className="space-y-3">
                  <a
                    href="https://interskateselfstorage.storageunitsoftware.com/pages/rent"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full px-4 py-2 text-center text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 transition-colors"
                    data-testid="rent-now-button"
                  >
                    Rent a Unit Now
                  </a>
                  <a
                    href="https://interskateselfstorage.storageunitsoftware.com/login"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full px-4 py-2 text-center text-sm font-medium text-green-600 border border-green-600 rounded-md hover:bg-green-50 transition-colors"
                    data-testid="make-payment-button"
                  >
                    Make a Payment
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;